// Launch configuration for the brand's connected ad account (Ad Launcher).
//
// The analytics connection tells the Ad Launcher WHICH account to launch into;
// these admin-set fields (vertical, page, pixel, default template, naming) are
// what make that account actually launchable. Editing is master-only
// server-side (POST/PATCH /api/windsor/.../accounts require master), so the
// UI is only surfaced to masters — from the Ad Launcher page's Config dialog,
// like the LP Express Meta Pixel pattern.
//
// Auto-prefill: when no launch account is saved yet, the form runs a read-only
// "detect from account" pass (GET /detect-config) that observes the brand's
// recently delivered campaigns via Windsor and pre-fills page/vertical/
// budget-cap/naming. A unanimous pixel also auto-fills; multiple observed
// pixels require an explicit admin selection.
// Detected values NEVER overwrite manually saved values without an explicit
// confirmation, and nothing persists except through the normal save.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { SearchableSelect } from "@/lib/picker-search";
import { Copy, Loader2, PlaneTakeoff, Sparkles, Plus, Trash2 } from "lucide-react";
import { reportAdLauncherError } from "@/lib/ad-launcher-error";
import { useAuth } from "@/hooks/use-auth";
import { useLaunchAccountDetection, type DetectionResponse } from "@/components/use-launch-account-detection";
import { AddLaunchAccountForm } from "@/components/add-launch-account-form";
import { LaunchPageDiscovery, useOrganicLaunchPages, useAccountLaunchPages, useConfirmedLaunchPages } from "@/components/launch-page-discovery";
import { launchAccountMappingFailure } from "@/lib/launch-account-mapping-error";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const WINDSOR_VERTICALS = ["leadgen", "ecom"] as const;

type LaunchAccountRow = {
  id: string;
  accountId: string;
  name: string;
  archivedAt?: string | null;
  vertical: string | null;
  leadSource: string | null;
  pageId: string | null;
  pixelId: string | null;
  defaultTemplate: string | null;
  economicsJson: Record<string, unknown> | null;
  namingJson: { brand?: string; product_line?: string } | null;
  missingFields: string[];
  launchReady: boolean;
  pagesJson?: Array<{ pageId: string; name: string; instagramActorId?: string | null; isDefault: boolean }> | null;
};

type ConfigPage = { pageId: string; name: string; instagramActorId: string; isDefault: boolean };
type MappingRequestContext = { generation: number };

// Form fields the detector can propose values for.
type DetectableKey = "vertical" | "pageId" | "pixelId" | "budgetCap" | "namingBrand" | "namingProductLine" | "leadSource";

export type MetaPage = {
  id?: string;
  name?: string;
  picture?: { data?: { url?: string; is_silhouette?: boolean } };
};

export function LaunchConfigSection({
  brandId,
  accountId,
  accountName,
  metaPages = [],
  metaPageStatus,
}: {
  brandId: string;
  accountId: string;
  accountName?: string;
  metaPages?: MetaPage[];
  metaPageStatus?: { metaLoading: boolean; metaError: boolean; metaFetching: boolean; onRetryMeta: () => void };
}) {
  const { toast } = useToast();
  const { user } = useAuth();
  const organicPagesQuery = useOrganicLaunchPages(brandId, user?.isMaster === true);
  const confirmedPagesQuery = useConfirmedLaunchPages(brandId, user?.isMaster === true);
  const pageDiscovery = {
    metaPages,
    ...metaPageStatus,
    organicPages: organicPagesQuery.data?.pages ?? [],
    confirmedPages: confirmedPagesQuery.data?.pages ?? [],
    confirmedLoading: confirmedPagesQuery.isLoading,
    confirmedError: confirmedPagesQuery.isError,
    confirmedFetching: confirmedPagesQuery.isFetching,
    onRetryConfirmed: () => { void confirmedPagesQuery.refetch(); },
    loading: organicPagesQuery.isLoading,
    error: organicPagesQuery.isError,
    fetching: organicPagesQuery.isFetching,
    onRetry: () => { void organicPagesQuery.refetch(); },
  };
  const [configAccountId, setConfigAccountId] = useState(accountId);
  const normalizedId = configAccountId.replace(/^act_/, "");
  const detectionAccountScope = useRef({ key: "" });
  const detectionScopeKey = JSON.stringify([brandId, normalizedId]);
  if (detectionAccountScope.current.key !== detectionScopeKey) {
    detectionAccountScope.current = { key: detectionScopeKey };
  }
  const { data: rows, isLoading } = useQuery<LaunchAccountRow[]>({
    queryKey: [`/api/windsor/brands/${brandId}/accounts`],
  });
  const { data: templates } = useQuery<{ id: string; kind: string; name?: string }[]>({
    queryKey: [`/api/windsor/brands/${brandId}/templates`],
    retry: false,
  });
  const existing = rows?.find((r) => r.accountId === normalizedId) ?? null;
  const accountPageDiscovery = useAccountLaunchPages(brandId, normalizedId, existing?.id, user?.isMaster === true && !isLoading);
  const editingLaunchOnly = !!existing && existing.accountId !== accountId.replace(/^act_/, "");

  const [vertical, setVertical] = useState("");
  const [accountLabel, setAccountLabel] = useState("");
  const [leadSource, setLeadSource] = useState("");
  const [pageId, setPageId] = useState("");
  const [pixelId, setPixelId] = useState("");
  const [budgetCapDollars, setBudgetCapDollars] = useState("");
  const [defaultTemplate, setDefaultTemplate] = useState("");
  const [namingBrand, setNamingBrand] = useState("");
  const [namingProductLine, setNamingProductLine] = useState("");
  const [namingPrefix, setNamingPrefix] = useState("");
  const [pages, setPages] = useState<ConfigPage[]>([]);
  const [newPageId, setNewPageId] = useState("");
  const [newPageName, setNewPageName] = useState("");
  const [newPageInstagram, setNewPageInstagram] = useState("");
  const [mappingConfirmationRequired, setMappingConfirmationRequired] = useState(false);
  const [mappingConfirmationError, setMappingConfirmationError] = useState<string | null>(null);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [newAccountId, setNewAccountId] = useState("");
  const [newAccountName, setNewAccountName] = useState("");
  const [newAccountVertical, setNewAccountVertical] = useState("");
  const [newAccountPage, setNewAccountPage] = useState("");
  const [newAccountPageName, setNewAccountPageName] = useState("");
  const [newAccountPixel, setNewAccountPixel] = useState("");
  const [newAccountPrefix, setNewAccountPrefix] = useState("");
  const [newAccountBudgetCap, setNewAccountBudgetCap] = useState("");
  const [newAccountNamingBrand, setNewAccountNamingBrand] = useState("");
  const [newAccountNamingProductLine, setNewAccountNamingProductLine] = useState("");
  const [newAccountLeadSource, setNewAccountLeadSource] = useState("");
  const [newAccountDefaultTemplate, setNewAccountDefaultTemplate] = useState("");
  const newAccountPageDiscovery = useAccountLaunchPages(brandId, newAccountId, undefined, addAccountOpen && user?.isMaster === true);
  const newAccountManualFields = useRef(new Set<string>());
  const newAccountValues = useRef({
    vertical: "", page: "", pageName: "", pixel: "", budget: "", namingBrand: "", namingProductLine: "", leadSource: "",
  });
  const [mappingConfirmationTarget, setMappingConfirmationTarget] = useState<"edit" | "add" | "archive">("edit");
  const [archiveConfirmationRow, setArchiveConfirmationRow] = useState<LaunchAccountRow | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<LaunchAccountRow | null>(null);
  const mappingRequestScope = useRef({ key: "", generation: 0 });
  const mappingScopeKey = JSON.stringify([brandId, normalizedId, newAccountId, addAccountOpen]);
  if (mappingRequestScope.current.key !== mappingScopeKey) {
    mappingRequestScope.current = { key: mappingScopeKey, generation: mappingRequestScope.current.generation + 1 };
  }
  const isCurrentMappingRequest = (context: MappingRequestContext | undefined) =>
    context?.generation === mappingRequestScope.current.generation;

  const clearMappingConfirmation = useCallback(() => {
    setMappingConfirmationRequired(false);
    setMappingConfirmationError(null);
    setMappingConfirmationTarget("edit");
    setArchiveConfirmationRow(null);
  }, []);

  // Confirmation belongs to this account and operation, not the next form.
  useEffect(() => {
    clearMappingConfirmation();
  }, [brandId, normalizedId, newAccountId, addAccountOpen, clearMappingConfirmation]);

  newAccountValues.current = {
    vertical: newAccountVertical,
    page: newAccountPage,
    pageName: newAccountPageName,
    pixel: newAccountPixel,
    budget: newAccountBudgetCap,
    namingBrand: newAccountNamingBrand,
    namingProductLine: newAccountNamingProductLine,
    leadSource: newAccountLeadSource,
  };

  const resetNewAccountConfig = useCallback(() => {
    setNewAccountVertical("");
    setNewAccountPage("");
    setNewAccountPageName("");
    setNewAccountPixel("");
    setNewAccountBudgetCap("");
    setNewAccountNamingBrand("");
    setNewAccountNamingProductLine("");
    setNewAccountLeadSource("");
    setNewAccountDefaultTemplate("");
    setNewAccountPrefix("");
    newAccountManualFields.current.clear();
  }, []);

  const applyNewAccountDetection = useCallback((response: DetectionResponse) => {
    const detected = response.detection;
    const canFill = (key: string, value: string | null | undefined, setter: (value: string) => void, numeric = false) => {
      if (
        value == null ||
        (numeric && !/^\d+$/.test(value)) ||
        newAccountValues.current[key as keyof typeof newAccountValues.current]?.trim() ||
        newAccountManualFields.current.has(key)
      ) return;
      setter(value);
    };
    if (detected.vertical.status === "detected" && (detected.vertical.value === "leadgen" || detected.vertical.value === "ecom")) {
      canFill("vertical", detected.vertical.value, setNewAccountVertical);
    }
    if (detected.pageId.status === "detected" && (detected.pageId.candidates == null || detected.pageId.candidates.length <= 1)) {
      canFill("page", detected.pageId.value, (value) => {
        setNewAccountPage(value);
        const exactMetaName = metaPages.find((page) => String(page.id) === value)?.name;
        if (exactMetaName && !newAccountValues.current.pageName.trim() && !newAccountManualFields.current.has("pageName")) {
          setNewAccountPageName(exactMetaName);
        }
      }, true);
    }
    if (detected.pixelId.status === "detected" && (detected.pixelId.candidates == null || detected.pixelId.candidates.length <= 1)) {
      canFill("pixel", detected.pixelId.value, setNewAccountPixel, true);
    }
    const capMinor = detected.dailyBudgetCapMinor.value;
    if (detected.dailyBudgetCapMinor.status === "detected" && typeof capMinor === "number" && Number.isFinite(capMinor) && capMinor > 0) {
      canFill("budget", (capMinor / 100).toFixed(2), setNewAccountBudgetCap);
    }
    if (detected.naming.status === "detected" && detected.naming.value) {
      canFill("namingBrand", detected.naming.value.brand, setNewAccountNamingBrand);
      canFill("namingProductLine", detected.naming.value.product_line, setNewAccountNamingProductLine);
    }
    if (detected.leadSource?.status === "detected" && detected.leadSource.value) {
      canFill("leadSource", detected.leadSource.value, setNewAccountLeadSource);
    }
  }, [metaPages]);

  const newAccountDetection = useLaunchAccountDetection(
    brandId,
    newAccountId,
    addAccountOpen && user?.isMaster === true,
    applyNewAccountDetection,
  );

  useEffect(() => setConfigAccountId(accountId), [accountId]);

  const availableAccountsQuery = useQuery<{ accounts: Array<{ accountId: string; accountName: string }>; source: "windsor" }>({
    queryKey: [`/api/windsor/brands/${brandId}/available-accounts`],
    enabled: addAccountOpen && user?.isMaster === true,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      try {
        const response = await apiRequest("GET", `/api/windsor/brands/${brandId}/available-accounts`);
        return (await response.json()) as { accounts: Array<{ accountId: string; accountName: string }>; source: "windsor" };
      } catch (error) {
        reportAdLauncherError(
          "launch-account-available-accounts",
          error instanceof Error ? error : new Error(String(error)),
        );
        throw error;
      }
    },
  });
  const availableAccountOptions = useMemo(() => {
    const configuredIds = new Set(
      (rows ?? [])
        .filter((row) => !row.archivedAt)
        .map((row) => row.accountId.replace(/^act_/i, "")),
    );
    return (availableAccountsQuery.data?.accounts ?? [])
      .map((account) => {
        const id = String(account.accountId).replace(/^act_/i, "");
        return { id, name: account.accountName };
      })
      .filter((account) => /^\d+$/.test(account.id) && !configuredIds.has(account.id))
      .map((account) => ({
        value: account.id,
        label: `${account.name} · act_${account.id}`,
        accountName: account.name,
      }));
  }, [availableAccountsQuery.data, rows]);
  const selectedAvailableAccount = availableAccountOptions.find((option) => option.value === newAccountId);

  // Which fields currently hold an auto-detected (not yet manually edited)
  // value — drives the "auto-detected" badges. Cleared per-field on edit.
  const [autoFilled, setAutoFilled] = useState<Set<DetectableKey>>(new Set());
  const [detection, setDetection] = useState<DetectionResponse | null>(null);
  // Detected values that CONFLICT with saved/typed values — applied only via
  // the explicit "use detected" confirmation.
  const [conflicts, setConflicts] = useState<Array<{ key: DetectableKey; label: string; current: string; detected: string; apply: () => void }>>([]);

  useEffect(() => {
    if (existing) {
      setVertical(existing.vertical || "");
      setAccountLabel(existing.name || "");
      setLeadSource(existing.leadSource || "");
      setPageId(existing.pageId || "");
      setPixelId(existing.pixelId || "");
      const capMinor = Number((existing.economicsJson as any)?.daily_budget_cap_minor);
      setBudgetCapDollars(Number.isFinite(capMinor) && capMinor > 0 ? (capMinor / 100).toFixed(2) : "");
      setDefaultTemplate(existing.defaultTemplate || "");
      setNamingBrand(existing.namingJson?.brand || "");
      setNamingProductLine(existing.namingJson?.product_line || "");
      setNamingPrefix((existing.namingJson as any)?.prefix || "");
      setPages(Array.isArray(existing.pagesJson)
        ? existing.pagesJson.map((page) => ({ ...page, instagramActorId: page.instagramActorId ?? "" }))
        : existing.pageId
          ? [{ pageId: existing.pageId, name: metaPages.find((page) => String(page.id) === existing.pageId)?.name ?? existing.pageId, instagramActorId: "", isDefault: true }]
          : []);
      setAutoFilled(new Set());
    }
  }, [existing?.id]);

  const addPage = () => {
    const id = newPageId.trim();
    if (!/^\d+$/.test(id) || pages.some((page) => page.pageId === id)) return;
    setPages((current) => [...current, {
      pageId: id,
      name: newPageName.trim() || id,
      instagramActorId: newPageInstagram.trim(),
      isDefault: current.length === 0,
    }]);
    setNewPageId("");
    setNewPageName("");
    setNewPageInstagram("");
  };

  const markManual = (key: DetectableKey) =>
    setAutoFilled((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

  // Apply a detection result to the form: blank fields get filled (and marked
  // auto-detected); non-blank differing fields become conflicts awaiting
  // explicit confirmation. Pure state — nothing saves until the Save button.
  const applyDetection = (resp: DetectionResponse) => {
    if (resp.connectedAccountId !== normalizedId) return;
    setDetection(resp);
    const d = resp.detection;
    const filled = new Set<DetectableKey>();
    const newConflicts: typeof conflicts = [];
    const consider = (
      key: DetectableKey, label: string, detected: string | null,
      current: string, set: (v: string) => void,
    ) => {
      if (!detected) return;
      if (!current.trim()) {
        set(detected);
        filled.add(key);
      } else if (current.trim() !== detected) {
        newConflicts.push({ key, label, current: current.trim(), detected, apply: () => { set(detected); } });
      }
    };
    consider("vertical", "Vertical", d.vertical.value, vertical, setVertical);
    // Lead source rides the same suggestion flow: the detected value is the
    // counter implied by the dominant lead optimization event — always a
    // suggestion the admin confirms/overrides, never silently persisted.
    consider("leadSource", "Lead source", d.leadSource?.value ?? null, leadSource, setLeadSource);
    consider("pageId", "Facebook Page ID", d.pageId.value, pageId, setPageId);
    // A single unanimously observed pixel can safely fill a blank field.
    // Multiple observed pixels remain explicit choices even when one has a
    // delivery majority. Differing saved values still require confirmation.
    if (d.pixelId.value) {
      if (
        !pixelId.trim()
        && d.pixelId.status === "detected"
        && (d.pixelId.candidates == null || d.pixelId.candidates.length === 1)
      ) {
        setPixelId(d.pixelId.value);
        filled.add("pixelId");
      } else if (pixelId.trim() && pixelId.trim() !== d.pixelId.value) {
        newConflicts.push({
          key: "pixelId",
          label: "Pixel ID",
          current: pixelId.trim(),
          detected: d.pixelId.value,
          apply: () => { setPixelId(d.pixelId.value!); },
        });
      }
    }
    const capDollars = d.dailyBudgetCapMinor.value != null ? (d.dailyBudgetCapMinor.value / 100).toFixed(2) : null;
    consider("budgetCap", "Daily budget cap", capDollars, budgetCapDollars, setBudgetCapDollars);
    consider("namingBrand", "Naming brand code", d.naming.value?.brand ?? null, namingBrand, setNamingBrand);
    consider("namingProductLine", "Naming product line", d.naming.value?.product_line ?? null, namingProductLine, setNamingProductLine);
    setAutoFilled((prev) => new Set([...prev, ...filled]));
    setConflicts(newConflicts);
  };

  // Auto-detection accepts the server's short-lived cache (rapid launcher
  // off/on toggling or dialog re-opens shouldn't repeat three Windsor reads);
  // the explicit "Detect from account" button always fetches fresh.
  const detect = useMutation({
    onMutate: () => detectionAccountScope.current,
    mutationFn: async ({ fresh }: { fresh: boolean }) => {
      const params = new URLSearchParams();
      if (fresh) params.set("fresh", "1");
      if (editingLaunchOnly && existing?.id) params.set("accountId", existing.id);
      const suffix = params.size ? `?${params.toString()}` : "";
      const res = await apiRequest("GET", `/api/windsor/brands/${brandId}/detect-config${suffix}`);
      return (await res.json()) as DetectionResponse;
    },
    onSuccess: (response, _variables, context) => {
      // Mutation callbacks may finish after the operator switched accounts.
      if (context === detectionAccountScope.current) applyDetection(response);
    },
    onError: (e: Error, _variables, context) => {
      if (context !== detectionAccountScope.current) return;
      reportAdLauncherError("config-detect", e);
      toast({ title: "Detection unavailable", description: "Couldn't detect settings from this account — you can enter them manually.", variant: "destructive" });
    },
  });

  // Auto-detect exactly once when the config opens with NO saved account, OR
  // when the saved account is missing a Facebook Page ID — a saved row with a
  // blank page must still get the auto-fill pass (detected values only fill
  // blanks; anything saved stays untouched or becomes an explicit conflict).
  // Throttled: automatic runs happen at most once per account per 24h
  // (localStorage-tracked) so opening the launcher repeatedly never loops
  // Windsor reads. Inside the cooldown a hint points at the explicit
  // "Detect from account" button, which always works and always fetches fresh.
  const AUTO_DETECT_COOLDOWN_MS = 24 * 60 * 60 * 1000;
  const autoDetectKey = `launcherAutoDetectAt:${normalizedId}`;
  const autoDetectRan = useRef(false);
  const [autoDetectCoolingDown, setAutoDetectCoolingDown] = useState(false);
  useEffect(() => {
    if (isLoading || autoDetectRan.current || !rows) return;
    autoDetectRan.current = true;
    if (existing && (existing.pageId || "").trim()) return; // nothing to fill
    let lastAt = 0;
    try { lastAt = Number(window.localStorage.getItem(autoDetectKey)) || 0; } catch { /* storage unavailable */ }
    if (Date.now() - lastAt < AUTO_DETECT_COOLDOWN_MS) {
      setAutoDetectCoolingDown(true); // ran recently and found nothing to keep — don't re-run automatically
      return;
    }
    try { window.localStorage.setItem(autoDetectKey, String(Date.now())); } catch { /* storage unavailable */ }
    detect.mutate({ fresh: false });
  }, [isLoading, rows, existing]);

  // The always-mounted launcher page owns the read-only Meta pages query so
  // both this picker and the Review summary consume one shared result.
  const pageOptions = useMemo(
    () =>
      metaPages
        .filter((p) => p.id)
        .map((p) => ({ value: String(p.id), label: p.name ? `${p.name} (${p.id})` : String(p.id) })),
    [metaPages],
  );
  // id→name map built from the same Meta pages list used for the fallback
  // picker. Used to annotate candidate chips and the auto-filled page ID field
  // with a human-readable name so admins can sanity-check without looking up
  // the ID. Windsor's facebook connector exposes page IDs via `actor_id` only
  // — no page name field is available from Windsor (page_id is known-broken,
  // returning empty strings on every row) — so this map is the sole name source.
  const pageNameById = useMemo(
    () => new Map(
      metaPages
        .filter((p) => p.id && p.name)
        .map((p) => [String(p.id), p.name!]),
    ),
    [metaPages],
  );

  const save = useMutation<any, Error, boolean, MappingRequestContext>({
    onMutate: (confirmed) => {
      if (!confirmed) clearMappingConfirmation();
      else setMappingConfirmationError(null);
      return { generation: ++mappingRequestScope.current.generation };
    },
    mutationFn: async (confirmCrossBrandMapping: boolean) => {
      if (pages.length > 0) {
        const ids = pages.map((page) => page.pageId.trim());
        if (ids.some((id) => !/^\d+$/.test(id)) || new Set(ids).size !== ids.length) {
          throw new Error("Page IDs must be numeric and unique.");
        }
        if (pages.filter((page) => page.isDefault).length !== 1) {
          throw new Error("Choose exactly one default Page.");
        }
        if (pages.some((page) => page.instagramActorId && !/^\d+$/.test(page.instagramActorId.trim()))) {
          throw new Error("Instagram actor IDs must be numeric.");
        }
      }
      // The record's name mirrors the analytics account name — it's the same
      // account, so we never ask for a separate label. An existing name that
      // is just the `act_<id>` fallback is upgraded to the fresh analytics
      // name instead of being re-persisted (server also self-heals).
      const isFallbackName = (n: string | undefined | null) =>
        !n || n.trim() === `act_${normalizedId}` || n.trim() === normalizedId || /^act_\d+$/.test(n.trim());
      const storedName = isFallbackName(existing?.name) ? "" : existing!.name;
      // Daily budget cap lives in economics.daily_budget_cap_minor — merge
      // with the existing economics targets (CPL/CPQL/MER stay manual).
      const economics: Record<string, unknown> = { ...(existing?.economicsJson ?? {}) };
      const capDollars = Number(budgetCapDollars);
      if (budgetCapDollars.trim() && Number.isFinite(capDollars) && capDollars > 0) {
        economics.daily_budget_cap_minor = Math.round(capDollars * 100);
      } else {
        delete economics.daily_budget_cap_minor;
      }
      const body = {
        accountId: normalizedId,
        name: existing && normalizedId !== accountId.replace(/^act_/, "")
          ? accountLabel.trim() || existing.name
          : storedName || detection?.accountName || accountName || existing?.name || `act_${normalizedId}`,
        vertical: vertical || null,
        // Leadgen accounts declare ONE lead counter (CPL is judged on it);
        // irrelevant for ecom, so it's cleared when the vertical isn't leadgen.
        leadSource: vertical === "leadgen" ? leadSource.trim() || null : null,
        pageId: pages.find((page) => page.isDefault)?.pageId || pageId.trim() || null,
        pagesJson: pages.length
          ? pages.map((page) => ({ ...page, instagramActorId: page.instagramActorId.trim() || undefined }))
          : null,
        confirmCrossBrandMapping,
        pixelId: pixelId.trim() || null,
        economics: Object.keys(economics).length > 0 ? economics : null,
        defaultTemplate: defaultTemplate || null,
        // Naming defaults for the dash-separated campaign-name convention
        // `Prefix - Objective - Structure - Funnel - Product - YYYYMMDD`.
        // The prefix defaults to "PersonaOS" when blank; brand/product_line
        // stay stored for older consumers.
        naming: namingBrand.trim() || namingProductLine.trim() || namingPrefix.trim()
          ? {
              ...(namingBrand.trim() ? { brand: namingBrand.trim() } : {}),
              ...(namingProductLine.trim() ? { product_line: namingProductLine.trim() } : {}),
              ...(namingPrefix.trim() ? { prefix: namingPrefix.trim() } : {}),
            }
          : null,
      };
      const res = existing
        ? await apiRequest("PATCH", `/api/windsor/brands/${brandId}/accounts/${existing.id}`, body)
        : await apiRequest("POST", `/api/windsor/brands/${brandId}/accounts`, body);
      return res.json();
    },
    onSuccess: (_data, _confirmed, context) => {
      queryClient.invalidateQueries({ queryKey: [`/api/windsor/brands/${brandId}/accounts`] });
      queryClient.invalidateQueries({ queryKey: [`/api/windsor/brands/${brandId}/launch-account`] });
      queryClient.invalidateQueries({ queryKey: [`/api/windsor/brands/${brandId}/launch-accounts`] });
      if (isCurrentMappingRequest(context)) {
        setConflicts([]);
        clearMappingConfirmation();
      }
      toast({ title: "Launch configuration saved" });
    },
    onError: (e: Error, confirmed, context) => {
      reportAdLauncherError("config-save", e);
      if (!isCurrentMappingRequest(context)) return;
      const failure = launchAccountMappingFailure(e);
      if (!confirmed && failure.confirmationRequired) {
        setMappingConfirmationRequired(true);
        setMappingConfirmationTarget("edit");
        return;
      }
      if (confirmed) setMappingConfirmationError(failure.message);
      toast({ title: "Save failed", description: failure.message, variant: "destructive" });
    },
  });

  const addLaunchAccount = useMutation<any, Error, boolean, MappingRequestContext>({
    onMutate: (confirmed) => {
      if (!confirmed) clearMappingConfirmation();
      else setMappingConfirmationError(null);
      return { generation: ++mappingRequestScope.current.generation };
    },
    mutationFn: async (confirmCrossBrandMapping: boolean) => {
      const account = newAccountId.trim().replace(/^act_/i, "");
      if (!selectedAvailableAccount || selectedAvailableAccount.value !== account || !newAccountName.trim()) {
        throw new Error("Choose an available Windsor account and enter an account name.");
      }
      if (!/^\d+$/.test(account) || !/^\d+$/.test(newAccountPage.trim())) {
        throw new Error("Enter a numeric Facebook Page identifier.");
      }
      const budgetCap = Number(newAccountBudgetCap);
      if (newAccountBudgetCap.trim() && (!Number.isFinite(budgetCap) || budgetCap <= 0)) {
        throw new Error("Daily budget cap must be a positive amount.");
      }
      const pagesJson = [{
        pageId: newAccountPage.trim(),
        name: newAccountPageName.trim() || newAccountPage.trim(),
        isDefault: true,
      }];
      const economics = newAccountBudgetCap.trim()
        ? { daily_budget_cap_minor: Math.round(budgetCap * 100) }
        : null;
      const naming = {
        ...(newAccountNamingBrand.trim() ? { brand: newAccountNamingBrand.trim() } : {}),
        ...(newAccountNamingProductLine.trim() ? { product_line: newAccountNamingProductLine.trim() } : {}),
        ...(newAccountPrefix.trim() ? { prefix: newAccountPrefix.trim() } : {}),
      };
      const response = await apiRequest("POST", `/api/windsor/brands/${brandId}/accounts`, {
        accountId: account,
        name: newAccountName.trim() || `act_${account}`,
        vertical: newAccountVertical || null,
        leadSource: newAccountVertical === "leadgen" ? newAccountLeadSource.trim() || null : null,
        pageId: newAccountPage.trim(),
        pagesJson,
        pixelId: newAccountPixel.trim() || null,
        economics,
        defaultTemplate: newAccountDefaultTemplate || null,
        naming: Object.keys(naming).length ? naming : null,
        confirmCrossBrandMapping,
      });
      return response.json();
    },
    onSuccess: (data, _confirmed, context) => {
      queryClient.invalidateQueries({ queryKey: [`/api/windsor/brands/${brandId}/accounts`] });
      queryClient.invalidateQueries({ queryKey: [`/api/windsor/brands/${brandId}/launch-accounts`] });
      queryClient.invalidateQueries({ queryKey: [`/api/windsor/brands/${brandId}/launch-account`] });
      if (isCurrentMappingRequest(context)) {
        clearMappingConfirmation();
        setAddAccountOpen(false);
        setNewAccountId("");
        setNewAccountName("");
        resetNewAccountConfig();
      }
      toast({ title: data.restored ? "Launch account restored" : "Launch account added" });
    },
    onError: (error: Error, confirmed, context) => {
      reportAdLauncherError("launch-account-add", error);
      if (!isCurrentMappingRequest(context)) return;
      const failure = launchAccountMappingFailure(error);
      if (!confirmed && failure.confirmationRequired) {
        setMappingConfirmationRequired(true);
        setMappingConfirmationTarget("add");
        return;
      }
      if (confirmed) setMappingConfirmationError(failure.message);
      toast({ title: "Account could not be added", description: failure.message, variant: "destructive" });
    },
  });

  const archiveAccount = useMutation({
    onMutate: (row: LaunchAccountRow & { confirmCrossBrandMapping?: boolean }) => {
      if (!row.confirmCrossBrandMapping) clearMappingConfirmation();
      else setMappingConfirmationError(null);
      return { generation: ++mappingRequestScope.current.generation };
    },
    mutationFn: async (row: LaunchAccountRow & { confirmCrossBrandMapping?: boolean }) =>
      apiRequest("PATCH", `/api/windsor/brands/${brandId}/accounts/${row.id}`, {
        archivedAt: new Date().toISOString(), confirmCrossBrandMapping: row.confirmCrossBrandMapping === true,
      }),
    onSuccess: (_data, _row, context) => {
      if (isCurrentMappingRequest(context)) clearMappingConfirmation();
      queryClient.invalidateQueries({ queryKey: [`/api/windsor/brands/${brandId}/accounts`] });
      queryClient.invalidateQueries({ queryKey: [`/api/windsor/brands/${brandId}/launch-accounts`] });
      queryClient.invalidateQueries({ queryKey: [`/api/windsor/brands/${brandId}/launch-account`] });
      toast({ title: "Launch account archived" });
    },
    onError: (error: Error, row, context) => {
      reportAdLauncherError("launch-account-archive", error);
      if (!isCurrentMappingRequest(context)) return;
      const failure = launchAccountMappingFailure(error);
      if (!row.confirmCrossBrandMapping && failure.confirmationRequired) {
        setMappingConfirmationRequired(true);
        setMappingConfirmationTarget("archive");
        setArchiveConfirmationRow(row);
        return;
      }
      if (row.confirmCrossBrandMapping) setMappingConfirmationError(failure.message);
      toast({ title: "Account could not be archived", description: failure.message, variant: "destructive" });
    },
  });

  // One-click: clone the most recent delivered campaign and set it as the
  // account's default template (existing extract flow, server-side).
  const cloneLatest = useMutation({
    mutationFn: async () => {
      const suffix = editingLaunchOnly && existing?.id ? `?accountId=${encodeURIComponent(existing.id)}` : "";
      const res = await apiRequest("POST", `/api/windsor/brands/${brandId}/clone/latest-default${suffix}`);
      return (await res.json()) as { defaultTemplate: string; sourceCampaignName: string; healthIssues: string[] };
    },
    onSuccess: (r) => {
      setDefaultTemplate(r.defaultTemplate);
      queryClient.invalidateQueries({ queryKey: [`/api/windsor/brands/${brandId}/accounts`] });
      queryClient.invalidateQueries({ queryKey: [`/api/windsor/brands/${brandId}/launch-account`] });
      queryClient.invalidateQueries({ queryKey: [`/api/windsor/brands/${brandId}/templates`] });
      toast({
        title: `Cloned "${r.sourceCampaignName}" as default template`,
        description: r.healthIssues?.length
          ? `Template health issues: ${r.healthIssues.join("; ")}`
          : undefined,
        variant: r.healthIssues?.length ? "destructive" : undefined,
      });
    },
    onError: (e: Error) => {
      reportAdLauncherError("config-clone", e);
      toast({ title: "Clone failed", description: "Couldn't clone from that account — try again.", variant: "destructive" });
    },
  });

  const builtinTemplates = (templates ?? []).filter((t) => t.kind === "builtin");
  const templateOptions = useMemo(() => {
    const opts = [
      { value: "__none__", label: "Not set" },
      ...builtinTemplates.map((t) => ({ value: t.id, label: t.id })),
    ];
    // Keep a cloned/saved default visible in the picker even though only
    // builtins are offered for manual selection.
    if (defaultTemplate && !opts.some((o) => o.value === defaultTemplate)) {
      opts.push({ value: defaultTemplate, label: defaultTemplate.startsWith("clone-") ? `cloned (${defaultTemplate.slice(0, 14)}…)` : defaultTemplate });
    }
    return opts;
  }, [builtinTemplates, defaultTemplate]);
  const missing = existing?.missingFields ?? [];

  // Field provenance badges (Task #2254): every field is permanently marked
  // as either Windsor-auto-detectable or manual one-time input, so admins can
  // see at a glance what still needs a human. AutoBadge additionally marks a
  // value that was just auto-filled (and not yet manually edited).
  const AutoBadge = ({ k }: { k: DetectableKey }) => (
    <>
      <Badge variant="outline" className="ml-1.5 border-sky-500/60 text-sky-600 text-[9px] uppercase tracking-wide px-1 py-0" data-testid={`badge-windsor-${k}`}>
        auto from Windsor
      </Badge>
      {autoFilled.has(k) && (
        <Badge variant="outline" className="ml-1 border-sky-500 bg-sky-500/10 text-sky-600 text-[9px] uppercase tracking-wide px-1 py-0" data-testid={`badge-detected-${k}`}>
          auto-detected
        </Badge>
      )}
    </>
  );
  const ManualBadge = ({ id }: { id: string }) => (
    <Badge variant="outline" className="ml-1.5 border-amber-500/70 text-amber-600 text-[9px] uppercase tracking-wide px-1 py-0" data-testid={`badge-manual-${id}`}>
      manual · one-time
    </Badge>
  );

  // Notes explaining why fields stayed blank (ambiguous / no data).
  const detectionNotes = useMemo(() => {
    if (!detection) return [];
    const d = detection.detection;
    const notes: string[] = [];
    for (const [label, f] of [
      ["Page ID", d.pageId], ["Vertical", d.vertical],
      ["Budget cap", d.dailyBudgetCapMinor], ["Naming", d.naming],
      // Lead-source notes are only relevant on leadgen accounts — on ecom the
      // field is hidden and "no lead events" is expected, not noteworthy.
      ...(vertical !== "ecom" && d.leadSource ? ([["Lead source", d.leadSource]] as const) : []),
    ] as const) {
      if ((f.status === "ambiguous" || f.status === "none") && f.note) notes.push(`${label}: ${f.note}`);
      else if (f.status === "majority" && f.note) notes.push(`${label}: ${f.note}`);
    }
    return notes;
  }, [detection, vertical]);
  const pixelDetection = detection?.detection.pixelId;

  return (
    <div className="space-y-3" data-testid="section-launch-config">
      <div className="flex items-center justify-between">
        <Label className="text-xs flex items-center gap-1.5">
          <PlaneTakeoff className="h-3.5 w-3.5" /> Launch configuration (Ad Launcher)
        </Label>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-[11px]"
            onClick={() => detect.mutate({ fresh: true })}
            disabled={detect.isPending || isLoading}
            data-testid="button-detect-from-account"
          >
            {detect.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
            Detect from account
          </Button>
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : existing?.launchReady ? (
            <Badge variant="secondary" className="bg-green-500/15 text-green-700 dark:text-green-400">launch ready</Badge>
          ) : (
            <Badge variant="outline" className="border-amber-500 text-amber-600">
              {existing ? `needs setup${missing.includes("pixelId") ? " · conversion pixel" : ""}` : "not set up"}
            </Badge>
          )}
        </div>
      </div>
      {user?.isMaster === true && (rows?.length ?? 0) > 0 && (
        <div className="space-y-2 rounded-md border p-3" data-testid="section-launch-account-admin">
          <div>
            <p className="text-xs font-medium">Launch accounts for this brand</p>
            <p className="text-[11px] text-muted-foreground">Choose a row to edit its launch settings. Archiving keeps existing launch history.</p>
          </div>
          <SearchableSelect
            value={rows?.some((row) => row.accountId === normalizedId) ? normalizedId : "__none__"}
            onValueChange={(value) => value !== "__none__" && setConfigAccountId(value)}
            testId="select-admin-launch-account"
            options={[
              { value: "__none__", label: "Select a launch account" },
              ...(rows ?? []).map((row) => ({
                value: row.accountId,
                label: `${row.name} · act_${row.accountId}${row.accountId === accountId.replace(/^act_/, "") ? " · Analytics connected" : " · Launch only"}`,
              })),
            ]}
          />
          <div className="space-y-1">
            {(rows ?? []).map((row) => (
              <div key={row.id} className="flex items-center justify-between gap-2 rounded bg-muted/20 px-2 py-1.5">
                <button type="button" className="min-w-0 truncate text-left text-xs font-medium hover:underline" onClick={() => setConfigAccountId(row.accountId)}>
                  {row.name} · act_{row.accountId}{row.accountId === accountId.replace(/^act_/, "") ? " · Analytics connected" : " · Launch only"}
                </button>
                <Button type="button" size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => setArchiveTarget(row)} disabled={archiveAccount.isPending} data-testid={`button-archive-launch-account-${row.id}`}>
                  Archive
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground" data-testid="text-launch-config-account-help">
        This setup belongs to <span className="font-medium text-foreground">{existing?.name || accountName || `act_${normalizedId}`}</span> (act_{normalizedId}).
        Detection only reads recent campaign history to suggest a Page, pixel, and launch defaults. Saving these settings does not change Meta permissions or reconnect the account.
        Brand users cannot edit them in the launch wizard.
      </p>
      {user?.isMaster === true && existing && normalizedId !== accountId.replace(/^act_/, "") && (
        <div className="space-y-1">
          <Label className="text-xs">Launch account name</Label>
          <Input value={accountLabel} onChange={(event) => setAccountLabel(event.target.value)} data-testid="input-launch-account-name" />
        </div>
      )}
      {detect.isPending && !detection && (
        <p className="text-[11px] text-sky-600 flex items-center gap-1" data-testid="text-detecting">
          <Loader2 className="h-3 w-3 animate-spin" /> Detecting settings from the account's recent campaigns…
        </p>
      )}
      {detectionNotes.length > 0 && (
        <div className="rounded-md border border-sky-500/30 bg-sky-500/5 px-2.5 py-1.5 space-y-0.5" data-testid="text-detection-notes">
          {detectionNotes.map((n, i) => (
            <p key={i} className="text-[11px] text-muted-foreground">{n}</p>
          ))}
        </div>
      )}
      {conflicts.length > 0 && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 px-2.5 py-2 space-y-1.5" data-testid="section-detection-conflicts">
          <p className="text-[11px] font-medium text-amber-700 dark:text-amber-400">
            Detection found different values than what's saved — confirm before overwriting:
          </p>
          {conflicts.map((c) => (
            <div key={c.key} className="flex items-center justify-between gap-2 text-[11px]">
              <span className="text-muted-foreground min-w-0 break-all">
                {c.label}: <span className="line-through">{c.current}</span> → <span className="font-medium text-foreground">{c.detected}</span>
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-5 px-1.5 text-[10px]"
                onClick={() => {
                  c.apply();
                  setAutoFilled((prev) => new Set([...prev, c.key]));
                  setConflicts((prev) => prev.filter((x) => x.key !== c.key));
                }}
                data-testid={`button-apply-detected-${c.key}`}
              >
                Use detected
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Vertical<AutoBadge k="vertical" /></Label>
          <SearchableSelect
            value={vertical || "__none__"}
            onValueChange={(v) => { setVertical(v === "__none__" ? "" : v); markManual("vertical"); }}
            testId="select-launch-vertical"
            options={[
              { value: "__none__", label: "Not set" },
              ...WINDSOR_VERTICALS.map((v) => ({ value: v, label: v === "ecom" ? "ecom (purchases)" : "leadgen (leads)" })),
            ]}
          />
          {autoFilled.has("vertical") && (
            <p className="text-[11px] text-sky-600" data-testid="text-vertical-suggestion">
              Suggested from recent campaign objectives — confirm it's right before saving (it changes required fields and how results are judged).
            </p>
          )}
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Daily budget cap ($)<AutoBadge k="budgetCap" /></Label>
          <Input
            value={budgetCapDollars}
            onChange={(e) => { setBudgetCapDollars(e.target.value); markManual("budgetCap"); }}
            placeholder="e.g. 100.00"
            inputMode="decimal"
            data-testid="input-launch-budget-cap"
          />
          <p className="text-[11px] text-muted-foreground">
            Default daily budget for launches. Target CPL/CPQL/MER stay manual one-time input — they're business declarations Windsor can't detect.
          </p>
        </div>
        {vertical === "leadgen" && (
          <div className="col-span-2 space-y-1">
            <Label className="text-xs">Lead source<AutoBadge k="leadSource" /></Label>
            <Input
              value={leadSource}
              onChange={(e) => { setLeadSource(e.target.value); markManual("leadSource"); }}
              placeholder='The ONE system that counts leads, e.g. "HubSpot MQLs"'
              data-testid="input-launch-lead-source"
            />
            <p className="text-[11px] text-muted-foreground">
              Leadgen success (CPL) is judged against this single declared lead counter — never Meta's own lead fields.
            </p>
            {/* Persistent suggestion: whenever the detected counter differs from
                what's typed, offer a one-click swap (the conflict panel is a
                one-shot snapshot at detect time; this stays available). */}
            {(() => {
              const detected = detection?.detection.leadSource?.value ?? null;
              if (!detected || detected === leadSource.trim()) return null;
              return (
                <div className="flex items-center justify-between gap-2 rounded-md border border-sky-500/30 bg-sky-500/5 px-2 py-1" data-testid="text-lead-source-detected">
                  <span className="text-[11px] text-muted-foreground min-w-0 break-all">
                    Detected from the account: <span className="font-medium text-foreground">{detected}</span>
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-5 px-1.5 text-[10px] shrink-0"
                    onClick={() => {
                      setLeadSource(detected);
                      setAutoFilled((prev) => new Set([...prev, "leadSource"]));
                      setConflicts((prev) => prev.filter((x) => x.key !== "leadSource"));
                    }}
                    data-testid="button-use-detected-lead-source"
                  >
                    Use this instead
                  </Button>
                </div>
              );
            })()}
          </div>
        )}
        <div className="col-span-2 space-y-1">
          <Label className="text-xs">Facebook Pages — appear on your ads</Label>
          <p className="text-[11px] text-muted-foreground">Add a connected Page by name, then save. The ad account above still pays for delivery.</p>
          <details className="rounded-md border px-3 py-2">
          <summary className="cursor-pointer text-xs text-muted-foreground">Manual default Page ID and historical suggestions</summary>
          <Input
            value={pageId}
            onChange={(e) => {
              const value = e.target.value;
              setPageId(value);
              if (pages.length <= 1) setPages(value.trim() ? [{ pageId: value.trim(), name: pageNameById.get(value.trim()) || value.trim(), instagramActorId: "", isDefault: true }] : []);
              markManual("pageId");
            }}
            placeholder="Page the ads run under"
            data-testid="input-launch-page-id"
          />
          {autoDetectCoolingDown && !pageId.trim() && !detect.isPending && !detect.data && (
            <p className="text-[11px] text-muted-foreground" data-testid="text-autodetect-cooldown">
              Auto-detection ran recently without finding a page — use “Detect from account” to check again.
            </p>
          )}
          {/* Ambiguous detection: the observed page ids are offered as
              one-click picks (delivery share shown) instead of a dead end.
              Page names are resolved from the Meta Ads connection's pages list
              when available; falls back to ID-only when no name is found. */}
          {(detect.data?.detection.pageId.candidates?.length ?? 0) > 0 && !pageId.trim() && (
            <div className="flex flex-wrap gap-1.5" data-testid="candidates-page-id">
              {detect.data!.detection.pageId.candidates!.map((c) => {
                const name = pageNameById.get(c.value);
                return (
                  <Button
                    key={c.value}
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[11px]"
                    onClick={() => { setPageId(c.value); markManual("pageId"); }}
                    data-testid={`button-page-candidate-${c.value}`}
                  >
                    {name ? (
                      <><span className="font-medium">{name}</span><span className="mx-1 text-muted-foreground">·</span><span className="font-mono">{c.value}</span></>
                    ) : (
                      <span className="font-mono">{c.value}</span>
                    )}
                    <span className="ml-1 text-muted-foreground">· {Math.round(c.share * 100)}%</span>
                  </Button>
                );
              })}
            </div>
          )}
          {/* When a page ID is set (auto-filled or typed), show its resolved
              name as a sanity-check hint — fail-soft when no name is known. */}
          {pageId.trim() && pageNameById.get(pageId.trim()) && (
            <p className="text-[11px] text-muted-foreground" data-testid="text-page-id-resolved-name">
              <span className="font-medium text-foreground">{pageNameById.get(pageId.trim())}</span>
            </p>
          )}
          {pageOptions.length > 0 && (
            <div className="space-y-0.5" data-testid="select-meta-page-fallback">
              <SearchableSelect
                value={pageOptions.some((o) => o.value === pageId.trim()) ? pageId.trim() : "__none__"}
                onValueChange={(v) => {
                  if (v === "__none__") return;
                  setPageId(v);
                  markManual("pageId");
                }}
                testId="select-launch-page-from-meta"
                options={[{ value: "__none__", label: "Pick from connected Meta pages…" }, ...pageOptions]}
              />
              <p className="text-[11px] text-muted-foreground">
                Pages from the brand's read-only Meta Ads connection — picking one fills the field above.
              </p>
            </div>
          )}
          </details>
          {user?.isMaster === true && (
            <div className="mt-3 space-y-2 rounded-md border p-3" data-testid="section-configured-pages">
              <LaunchPageDiscovery key={`${brandId}:${normalizedId}`} {...pageDiscovery} {...accountPageDiscovery} immediate label="Add a Facebook Page" testId="config-page-discovery" excludedIds={pages.map(page => page.pageId)} onSelect={(page) => {
                setPages(current => current.some(item => item.pageId === page.pageId) ? current : [...current, {
                  pageId: page.pageId, name: page.name, instagramActorId: "", isDefault: current.length === 0,
                }]);
                if (pages.length === 0) setPageId(page.pageId);
                markManual("pageId");
              }} />
              <div>
                <p className="text-xs font-medium">Pages available to launch from after saving</p>
                <p className="text-[11px] text-muted-foreground">Adding a Page keeps your existing default. Choose “Make default” only if you want to change it.</p>
              </div>
              {pages.map((page, index) => (
                <div key={`${page.pageId}-${index}`} className="grid grid-cols-1 gap-2 rounded-md bg-muted/20 p-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
                  <Input
                    aria-label={`Page ID ${index + 1}`}
                    inputMode="numeric"
                    value={page.pageId}
                    onChange={(event) => setPages((current) => current.map((item, i) => i === index ? { ...item, pageId: event.target.value } : item))}
                    placeholder="Numeric Page ID"
                    data-testid={`input-config-page-id-${index}`}
                  />
                  <Input
                    aria-label={`Page name ${index + 1}`}
                    value={page.name}
                    onChange={(event) => setPages((current) => current.map((item, i) => i === index ? { ...item, name: event.target.value } : item))}
                    placeholder="Page name"
                    data-testid={`input-config-page-name-${index}`}
                  />
                  <Input
                    aria-label={`Instagram actor ID ${index + 1}`}
                    inputMode="numeric"
                    value={page.instagramActorId}
                    onChange={(event) => setPages((current) => current.map((item, i) => i === index ? { ...item, instagramActorId: event.target.value } : item))}
                    placeholder="Instagram actor ID (optional)"
                    data-testid={`input-config-instagram-actor-${index}`}
                  />
                  <div className="flex items-center gap-2">
                    <Button type="button" size="sm" variant={page.isDefault ? "default" : "outline"} onClick={() => {
                      setPages((current) => current.map((item, i) => ({ ...item, isDefault: i === index })));
                      setPageId(page.pageId);
                    }} data-testid={`button-config-page-default-${index}`}>
                      {page.isDefault ? "Default" : "Make default"}
                    </Button>
                    <Button type="button" size="icon" variant="ghost" aria-label={`Remove ${page.name || page.pageId}`} onClick={() => {
                      const next = pages.filter((_, i) => i !== index);
                      if (page.isDefault && next.length) {
                        next[0] = { ...next[0], isDefault: true };
                        setPageId(next[0].pageId);
                      }
                      setPages(next);
                    }} data-testid={`button-remove-config-page-${index}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <details className="rounded-md border px-3 py-2">
              <summary className="cursor-pointer text-xs text-muted-foreground">Enter a Page manually</summary>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
                <Input value={newPageId} onChange={(event) => setNewPageId(event.target.value)} inputMode="numeric" placeholder="Numeric Page ID" data-testid="input-new-config-page-id" />
                <Input value={newPageName} onChange={(event) => setNewPageName(event.target.value)} placeholder="Page name" data-testid="input-new-config-page-name" />
                <Input value={newPageInstagram} onChange={(event) => setNewPageInstagram(event.target.value)} inputMode="numeric" placeholder="Instagram actor ID (optional)" data-testid="input-new-config-instagram-actor" />
                <Button type="button" variant="outline" onClick={addPage} disabled={!/^\d+$/.test(newPageId.trim()) || pages.some((page) => page.pageId === newPageId.trim())} data-testid="button-add-config-page">
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add Page
                </Button>
              </div>
              </details>
            </div>
          )}
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Conversion pixel<AutoBadge k="pixelId" /></Label>
          <Input
            value={pixelId}
            onChange={(e) => { setPixelId(e.target.value); markManual("pixelId"); }}
            placeholder="Conversion tracking pixel"
            data-testid="input-launch-pixel-id"
          />
          <p className="text-[11px] text-muted-foreground">
            The pixel records conversion activity from these ads. Detection is read-only; it does not grant access or alter Meta settings.
            {(!pixelId.trim() || missing.includes("pixelId")) && " If this is missing, detect from the account, select the right pixel, then save this setup."}
          </p>
          {!pixelId.trim() && pixelDetection?.note && (
            <p className="text-[11px] text-sky-700 dark:text-sky-400" data-testid="text-pixel-detection-note">
              {pixelDetection.note}
            </p>
          )}
          {(pixelDetection?.candidates?.length ?? 0) > 0 && !pixelId.trim() && (
            <div className="flex flex-wrap gap-1.5" data-testid="candidates-pixel-id">
              {pixelDetection!.candidates!.map((c) => (
                <Button
                  key={c.value}
                  size="sm"
                  variant="outline"
                  className="h-6 px-2 text-[11px] font-mono"
                  onClick={() => { setPixelId(c.value); markManual("pixelId"); }}
                  data-testid={`button-pixel-candidate-${c.value}`}
                >
                  {c.value} · {Math.round(c.share * 100)}% of recent delivery
                </Button>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Naming: prefix (optional)</Label>
          <Input
            value={namingPrefix}
            onChange={(e) => setNamingPrefix(e.target.value)}
            placeholder="PersonaOS"
            data-testid="input-launch-naming-prefix"
          />
          <p className="text-[11px] text-muted-foreground">
            First segment of campaign names (Prefix - Objective - Structure - Funnel - Product - Date). Defaults to "PersonaOS" when blank.
          </p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Naming: brand code<AutoBadge k="namingBrand" /></Label>
          <Input
            value={namingBrand}
            onChange={(e) => { setNamingBrand(e.target.value); markManual("namingBrand"); }}
            placeholder='Short code for names, e.g. "PB"'
            data-testid="input-launch-naming-brand"
          />
          <p className="text-[11px] text-muted-foreground">
            Legacy naming code — kept for older consumers; new campaign names use the prefix above.
          </p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Naming: product line (optional)<AutoBadge k="namingProductLine" /></Label>
          <Input
            value={namingProductLine}
            onChange={(e) => { setNamingProductLine(e.target.value); markManual("namingProductLine"); }}
            placeholder='e.g. "Balm"'
            data-testid="input-launch-naming-product-line"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Default template<ManualBadge id="default-template" /></Label>
          <SearchableSelect
            value={defaultTemplate || "__none__"}
            onValueChange={(v) => setDefaultTemplate(v === "__none__" ? "" : v)}
            testId="select-launch-template"
            options={templateOptions}
          />
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          onClick={() => save.mutate(false)}
          disabled={save.isPending || isLoading}
          data-testid="button-launch-config-save"
        >
          {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {existing ? "Update launch config" : "Create launch config"}
        </Button>
        {existing && existing.vertical && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => cloneLatest.mutate()}
            disabled={cloneLatest.isPending}
            data-testid="button-clone-latest-default"
          >
            {cloneLatest.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}
            Clone latest campaign as default template
          </Button>
        )}
      </div>
      {mappingConfirmationRequired && user?.isMaster === true && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3" role="alert" data-testid="alert-cross-brand-mapping">
          <p className="text-xs text-amber-800">This account is already mapped to another brand. Confirm only if this shared access is intentional.</p>
          <Button type="button" size="sm" variant="outline" disabled={save.isPending || addLaunchAccount.isPending || archiveAccount.isPending} onClick={() => {
            if (mappingConfirmationTarget === "add") {
              if (!addAccountOpen || !selectedAvailableAccount) {
                setMappingConfirmationError("This confirmation is no longer current. Start the save again.");
                return;
              }
              addLaunchAccount.mutate(true);
            } else if (mappingConfirmationTarget === "archive") {
              if (!archiveConfirmationRow) {
                setMappingConfirmationError("This confirmation is no longer current. Start the archive again.");
                return;
              }
              archiveAccount.mutate({ ...archiveConfirmationRow, confirmCrossBrandMapping: true });
            } else save.mutate(true);
          }} data-testid="button-confirm-cross-brand-mapping">
            {(save.isPending || addLaunchAccount.isPending || archiveAccount.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {save.isPending || addLaunchAccount.isPending || archiveAccount.isPending
              ? mappingConfirmationTarget === "archive" ? "Archiving…" : "Saving…"
              : mappingConfirmationTarget === "archive" ? "Confirm and archive" : "Confirm and save mapping"}
          </Button>
          {mappingConfirmationError && <p className="basis-full text-xs text-destructive" data-testid="error-cross-brand-mapping">{mappingConfirmationError}</p>}
        </div>
      )}
      {user?.isMaster === true && (
        <div className="border-t pt-3">
          {!addAccountOpen ? (
            <Button type="button" size="sm" variant="outline" onClick={() => setAddAccountOpen(true)} data-testid="button-add-launch-account">
              <Plus className="mr-1.5 h-4 w-4" /> Set up another ad account
            </Button>
          ) : (
            <AddLaunchAccountForm
              pageDiscovery={{ ...pageDiscovery, ...newAccountPageDiscovery }}
              accountId={newAccountId}
              accountName={newAccountName}
              onAccountIdChange={(value) => {
                const selected = availableAccountOptions.find((option) => option.value === value);
                if (!selected) return;
                if (selected.value !== newAccountId) {
                  resetNewAccountConfig();
                  newAccountManualFields.current.clear();
                }
                setNewAccountId(selected.value);
                setNewAccountName(selected.accountName);
              }}
              onAccountNameChange={setNewAccountName}
              availableAccounts={availableAccountOptions}
              catalogLoading={availableAccountsQuery.isLoading}
              catalogError={availableAccountsQuery.isError}
              catalogFetching={availableAccountsQuery.isFetching}
              onRetryCatalog={() => availableAccountsQuery.refetch()}
              detectionStatus={newAccountDetection.status}
              detectionResult={newAccountDetection.result}
              onRetryDetection={newAccountDetection.retry}
              manualFields={new Set(newAccountManualFields.current)}
              metaPages={metaPages}
              vertical={newAccountVertical}
              onVerticalChange={(value) => { setNewAccountVertical(value === "__none__" ? "" : value); newAccountManualFields.current.add("vertical"); }}
              pageId={newAccountPage}
              onPageIdChange={(value) => { setNewAccountPage(value); newAccountManualFields.current.add("page"); }}
              pageName={newAccountPageName}
              onPageNameChange={(value) => { setNewAccountPageName(value); newAccountManualFields.current.add("pageName"); }}
              pixelId={newAccountPixel}
              onPixelIdChange={(value) => { setNewAccountPixel(value); newAccountManualFields.current.add("pixel"); }}
              budgetCap={newAccountBudgetCap}
              onBudgetCapChange={(value) => { setNewAccountBudgetCap(value); newAccountManualFields.current.add("budget"); }}
              namingBrand={newAccountNamingBrand}
              onNamingBrandChange={(value) => { setNewAccountNamingBrand(value); newAccountManualFields.current.add("namingBrand"); }}
              namingProductLine={newAccountNamingProductLine}
              onNamingProductLineChange={(value) => { setNewAccountNamingProductLine(value); newAccountManualFields.current.add("namingProductLine"); }}
              namingPrefix={newAccountPrefix}
              onNamingPrefixChange={setNewAccountPrefix}
              leadSource={newAccountLeadSource}
              onLeadSourceChange={(value) => { setNewAccountLeadSource(value); newAccountManualFields.current.add("leadSource"); }}
              defaultTemplate={newAccountDefaultTemplate}
              onDefaultTemplateChange={(value) => setNewAccountDefaultTemplate(value === "__none__" ? "" : value)}
              templates={builtinTemplates}
              saving={addLaunchAccount.isPending}
              onSave={() => addLaunchAccount.mutate(false)}
              onCancel={() => { setAddAccountOpen(false); setNewAccountId(""); setNewAccountName(""); resetNewAccountConfig(); }}
            />
          )}
        </div>
      )}
      <AlertDialog open={!!archiveTarget} onOpenChange={(open) => !open && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this launch account?</AlertDialogTitle>
            <AlertDialogDescription>
              {archiveTarget?.name} will no longer be selectable for new launches. Existing plans and history remain available.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (archiveTarget) archiveAccount.mutate(archiveTarget);
              setArchiveTarget(null);
            }} data-testid="button-confirm-archive-launch-account">Archive account</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
